import React from 'react';
import { Container, Typography, Paper, Link } from '@mui/material';
import { styled } from '@mui/system';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
}));

function CommunityGuidelines() {
    return (
        <Container maxWidth="md" style={{ marginTop: '50px' }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
                COMMUNITY GUIDELINES FOR BORROWED, INC.
            </Typography>
            <StyledPaper>
                <Typography variant="subtitle1" gutterBottom>
                    <strong>Effective Date: [March 12, 2025]</strong>
                </Typography>
                <Typography paragraph>
                    Welcome to getborrowed.com (the <strong>"Borrowed Site"</strong>). Our Community Guidelines are designed to ensure that all users—Owners, Lenders, and Borrowers—engage in respectful, safe, and fair interactions while using our platform.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    1. Introduction and Purpose
                </Typography>
                <Typography paragraph>
                    These guidelines establish the standards for behavior and conduct within our community. They are intended to foster a positive environment where everyone can confidently engage in lending and borrowing transactions. By using the Borrowed Service, you agree to abide by these guidelines.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    2. Respectful Communication and Conduct
                </Typography>
                <Typography paragraph>
                    We expect all community members to interact with each other in a respectful and professional manner. This includes:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Using polite and considerate language in all communications.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Avoiding personal attacks, harassment, or discriminatory remarks.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Refraining from posting content that could be interpreted as hate speech, threats, or defamation.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Being open to constructive criticism and resolving disputes amicably.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    3. Content Standards and Listings
                </Typography>
                <Typography paragraph>
                    All content posted on the platform must adhere to the following standards:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Listings must be accurate, truthful, and reflect the actual condition of the items.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Prohibited content includes illegal items, counterfeit goods, items that violate intellectual property rights, and any content that could endanger the community.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Photographs and descriptions must be clear, genuine, and free from misleading information.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Promotional or commercial content that is not directly related to lending or borrowing is not allowed.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    4. Posting and Interaction Guidelines
                </Typography>
                <Typography paragraph>
                    When engaging on our platform, please observe the following:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Only post or comment on topics that are relevant to the Borrowed community.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Avoid excessive self-promotion or spamming in listings, reviews, and messages.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Any form of fraudulent behavior or misrepresentation will result in immediate action.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Respect the privacy of other users by not sharing personal information without consent.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    5. Transaction Transparency and Honesty
                </Typography>
                <Typography paragraph>
                    For a healthy and trustworthy community, transparency in all transactions is vital. This means:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Being upfront about any fees, charges, or conditions related to lending transactions.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Immediately notifying the other party in case of any issues or discrepancies.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Adhering to agreed-upon terms and timelines for borrowing and lending.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    6. Safety and Security
                </Typography>
                <Typography paragraph>
                    Your safety is our priority. To protect our community, please note the following:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Verify the identity and reputation of users before engaging in transactions.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Report any suspicious activity or behavior to our support team immediately.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Always meet in safe, public places when exchanging items, if applicable.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Use our in-app communication channels rather than external methods to help preserve privacy.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    7. Dispute Resolution and Mediation
                </Typography>
                <Typography paragraph>
                    While we strive for a conflict-free community, disputes may occasionally arise. Our approach includes:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Encouraging users to communicate directly and work towards an amicable resolution.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Providing mediation services in cases where direct resolution is not possible.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Not taking sides in disputes, but ensuring that our community guidelines are upheld.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    8. Reporting Violations and Enforcement
                </Typography>
                <Typography paragraph>
                    We rely on our community to help keep the platform safe and welcoming. If you witness behavior that violates these guidelines:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Use the in-app report feature to alert us.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Provide as much detail as possible to help us assess the situation.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Understand that repeated violations can result in suspension or termination of your account.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    9. Accountability and Continuous Improvement
                </Typography>
                <Typography paragraph>
                    We believe in accountability and continuous improvement:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Users are responsible for their actions on the platform.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Our team will periodically review and update these guidelines to meet evolving community needs.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Feedback is welcome; please share your suggestions on how we can enhance the community experience.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    10. Additional Guidelines
                </Typography>
                <Typography paragraph>
                    In addition to the points outlined above, please observe the following:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Do not attempt to game the rating or review system.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Avoid making defamatory or baseless claims about other users.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Respect any additional guidelines that may apply to specific features or promotions on our platform.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    11. Modifications to the Guidelines
                </Typography>
                <Typography paragraph>
                    Borrowed, Inc. reserves the right to modify these Community Guidelines at any time. Any significant changes will be communicated through our website and, if possible, via email.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    12. Contact and Support
                </Typography>
                <Typography paragraph>
                    If you have any questions about these guidelines or need assistance, please reach out to us at&nbsp;
                    <Link href="mailto:support@getborrowed.com">support@getborrowed.com</Link>. We are committed to ensuring a positive experience for everyone on our platform.
                </Typography>

                <Typography paragraph align="center">
                    © Borrowed, Inc. All rights reserved. [Last Updated: March 12, 2025]
                </Typography>
            </StyledPaper>
        </Container>
    );
}

export default CommunityGuidelines;
